import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MarkdownEditor } from '../../molecules/MarkdownEditor';
import { FileUploader } from '../../molecules/FileUploader';
import { Modal } from '../../molecules/Modal';
import { programService } from '../../../services/programService';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const FlexRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const FlexGrow = styled.div`
  flex: 1;
`;

const AddNewButton = styled.button`
  padding: 10px 16px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #218838;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  color: white;

  &:hover:not(:disabled) {
    background-color: #5a6268;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #007bff;
  color: white;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 13px;
  margin-top: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  border: 2px solid ${props => props.$active ? '#007bff' : '#dee2e6'};
  border-radius: 6px;
  background-color: ${props => props.$active ? '#007bff' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #007bff;
    background-color: ${props => props.$active ? '#0056b3' : '#e7f3ff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddProgramModal = ({ isOpen, onClose, onSubmit, studentId, existingPrograms = [] }) => {
  const [formData, setFormData] = useState({
    majorCategory: '',
    middleCategory: '',
    minorCategory: '',
    detailedGoal: '',
    instructionMethod: '',
    repetitionCount: 10,
    successCriteria: 9,
    behaviorType: 'positive_above',
    programSuccessCriteria: '',
    programSuccessMode: 'consecutive',
    promptingMethod: '',
    reinforcementSchedule: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNewMajorInput, setShowNewMajorInput] = useState(false);
  const [showNewMiddleInput, setShowNewMiddleInput] = useState(false);
  const [showNewMinorInput, setShowNewMinorInput] = useState(false);
  const [newMajorCategory, setNewMajorCategory] = useState('');
  const [newMiddleCategory, setNewMiddleCategory] = useState('');
  const [newMinorCategory, setNewMinorCategory] = useState('');
  const [referenceImages, setReferenceImages] = useState([]);
  const [referenceVideos, setReferenceVideos] = useState([]);
  const [useFreeForm, setUseFreeForm] = useState(false);
  const [freeFormContent, setFreeFormContent] = useState('');
  const [programTemplates, setProgramTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // 기존 프로그램들로부터 카테고리 추출
  const existingMajorCategories = [...new Set(existingPrograms.map(p => p.program?.majorCategory).filter(Boolean))];
  const existingMiddleCategories = formData.majorCategory && formData.majorCategory !== '__new__'
    ? [...new Set(existingPrograms
        .filter(p => p.program?.majorCategory === formData.majorCategory)
        .map(p => p.program?.middleCategory)
        .filter(Boolean))]
    : [];

  // 기존 프로그램 목록 (소주제) 추출
  const existingMinorCategories = formData.majorCategory && formData.middleCategory &&
    formData.majorCategory !== '__new__' && formData.middleCategory !== '__new__'
    ? [...new Set(existingPrograms
        .filter(p =>
          p.program?.majorCategory === formData.majorCategory &&
          p.program?.middleCategory === formData.middleCategory
        )
        .map(p => p.program?.minorCategory)
        .filter(Boolean))]
    : [];

  // 기존 프로그램 데이터 맵 (자동완성용)
  const existingProgramsMap = existingPrograms.reduce((acc, p) => {
    if (p.program) {
      const key = `${p.program.majorCategory}|${p.program.middleCategory}|${p.program.minorCategory}`;
      acc[key] = p.program;
    }
    return acc;
  }, {});

  // Fetch program templates when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProgramTemplates();
    }
  }, [isOpen]);

  const fetchProgramTemplates = async () => {
    try {
      const templates = await programService.getAllProgramTemplates();
      setProgramTemplates(templates);
    } catch (error) {
      console.error('Failed to fetch program templates:', error);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);

    if (templateId) {
      const template = programTemplates.find(t => t.id === templateId);
      if (template) {
        // Fill in all form fields from template
        setFormData({
          majorCategory: template.majorCategory || '',
          middleCategory: template.middleCategory || '',
          minorCategory: template.minorCategory || '',
          detailedGoal: template.detailedGoal || '',
          instructionMethod: template.instructionMethod || '',
          repetitionCount: template.repetitionCount || 10,
          successCriteria: template.successCriteria || 9,
          behaviorType: template.behaviorType || 'positive_above',
          programSuccessCriteria: template.programSuccessCriteria || 5,
          programSuccessMode: template.programSuccessMode || 'consecutive',
          promptingMethod: template.promptingMethod || '',
          reinforcementSchedule: template.reinforcementSchedule || ''
        });

        // Fill in markdown mode data
        setUseFreeForm(template.useFreeForm || false);
        setFreeFormContent(template.freeFormContent || '');

        // Fill in reference files
        setReferenceImages(template.referenceFiles?.images || []);
        setReferenceVideos(template.referenceFiles?.videos || []);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // 모달이 닫힐 때 폼 초기화
      setFormData({
        majorCategory: '',
        middleCategory: '',
        minorCategory: '',
        detailedGoal: '',
        instructionMethod: '',
        repetitionCount: 10,
        successCriteria: 9,
        behaviorType: 'positive_above',
        programSuccessCriteria: '',
        programSuccessMode: 'consecutive',
        promptingMethod: '',
        reinforcementSchedule: ''
      });
      setErrors({});
      setShowNewMajorInput(false);
      setShowNewMiddleInput(false);
      setShowNewMinorInput(false);
      setNewMajorCategory('');
      setNewMiddleCategory('');
      setNewMinorCategory('');
      setReferenceImages([]);
      setReferenceVideos([]);
      setUseFreeForm(false);
      setFreeFormContent('');
      setSelectedTemplateId('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // "새 항목 추가" 선택 시 특별 처리
    if (name === 'majorCategory' && value === '__new__') {
      setShowNewMajorInput(true);
      setFormData(prev => ({ ...prev, middleCategory: '' }));
      setShowNewMiddleInput(false);
      setNewMiddleCategory('');
      return;
    }

    if (name === 'middleCategory' && value === '__new__') {
      setShowNewMiddleInput(true);
      return;
    }

    if (name === 'minorCategory' && value === '__new__') {
      setShowNewMinorInput(true);
      return;
    }

    // 일반 값 업데이트
    setFormData(prev => ({
      ...prev,
      [name]: name === 'repetitionCount' || name === 'successCriteria' || name === 'programSuccessCriteria'
        ? parseInt(value) || 0
        : value
    }));

    // minorCategory 선택 시 기존 프로그램 데이터로 자동완성
    if (name === 'minorCategory' && value && value !== '__new__') {
      const key = `${formData.majorCategory}|${formData.middleCategory}|${value}`;
      const existingProgram = existingProgramsMap[key];

      if (existingProgram) {
        setFormData(prev => ({
          ...prev,
          minorCategory: value,
          detailedGoal: existingProgram.detailedGoal || prev.detailedGoal,
          instructionMethod: existingProgram.instructionMethod || prev.instructionMethod,
          promptingMethod: existingProgram.promptingMethod || prev.promptingMethod,
          reinforcementSchedule: existingProgram.reinforcementSchedule || prev.reinforcementSchedule,
          repetitionCount: existingProgram.repetitionCount || prev.repetitionCount,
          successCriteria: existingProgram.successCriteria || prev.successCriteria,
          behaviorType: existingProgram.behaviorType || prev.behaviorType,
          programSuccessCriteria: existingProgram.programSuccessCriteria || prev.programSuccessCriteria,
          programSuccessMode: existingProgram.programSuccessMode || prev.programSuccessMode,
        }));
        return;
      }
    }

    // 에러 메시지 제거
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // 대주제가 변경되면 중주제, 소주제 초기화
    if (name === 'majorCategory') {
      setFormData(prev => ({ ...prev, middleCategory: '', minorCategory: '' }));
      setShowNewMiddleInput(false);
      setShowNewMinorInput(false);
      setNewMiddleCategory('');
      setNewMinorCategory('');
      setShowNewMajorInput(false);
      setNewMajorCategory('');
    }

    // 중주제가 변경되면 소주제 초기화
    if (name === 'middleCategory') {
      setFormData(prev => ({ ...prev, minorCategory: '' }));
      setShowNewMiddleInput(false);
      setShowNewMinorInput(false);
      setNewMiddleCategory('');
      setNewMinorCategory('');
    }

    // 소주제가 변경되면
    if (name === 'minorCategory') {
      setShowNewMinorInput(false);
      setNewMinorCategory('');
    }
  };

  const handleAddNewMajor = () => {
    if (newMajorCategory.trim()) {
      setFormData(prev => ({ ...prev, majorCategory: newMajorCategory.trim() }));
      setShowNewMajorInput(false);
      setNewMajorCategory('');
      // 에러 메시지 제거
      if (errors.majorCategory) {
        setErrors(prev => ({ ...prev, majorCategory: '' }));
      }
    }
  };

  const handleAddNewMiddle = () => {
    if (newMiddleCategory.trim()) {
      setFormData(prev => ({ ...prev, middleCategory: newMiddleCategory.trim() }));
      setShowNewMiddleInput(false);
      setNewMiddleCategory('');
      // 에러 메시지 제거
      if (errors.middleCategory) {
        setErrors(prev => ({ ...prev, middleCategory: '' }));
      }
    }
  };

  const handleAddNewMinor = () => {
    if (newMinorCategory.trim()) {
      setFormData(prev => ({ ...prev, minorCategory: newMinorCategory.trim() }));
      setShowNewMinorInput(false);
      setNewMinorCategory('');
      // 에러 메시지 제거
      if (errors.minorCategory) {
        setErrors(prev => ({ ...prev, minorCategory: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.majorCategory.trim()) {
      newErrors.majorCategory = '대주제를 입력해주세요.';
    }
    if (!formData.middleCategory.trim()) {
      newErrors.middleCategory = '중주제를 입력해주세요.';
    }
    if (!formData.minorCategory.trim()) {
      newErrors.minorCategory = '소주제를 입력해주세요.';
    }

    // 마크다운 모드일 때는 freeFormContent만 검증
    if (useFreeForm) {
      if (!freeFormContent.trim()) {
        newErrors.freeFormContent = '프로그램 내용을 입력해주세요.';
      }
    } else {
      // 폼 모드일 때는 기존 필드들 검증
      if (!formData.detailedGoal.trim()) {
        newErrors.detailedGoal = '세부 목표를 입력해주세요.';
      }
      if (!formData.instructionMethod.trim()) {
        newErrors.instructionMethod = '지시 방법을 입력해주세요.';
      }
      if (!formData.promptingMethod.trim()) {
        newErrors.promptingMethod = '촉구 방법을 입력해주세요.';
      }
      if (!formData.reinforcementSchedule.trim()) {
        newErrors.reinforcementSchedule = '강화 스케줄을 입력해주세요.';
      }
    }

    if (formData.repetitionCount < 1) {
      newErrors.repetitionCount = '반복 횟수는 1 이상이어야 합니다.';
    }
    if (formData.successCriteria < 1) {
      newErrors.successCriteria = '도달 성공 기준은 1 이상이어야 합니다.';
    }
    if (formData.successCriteria > formData.repetitionCount) {
      newErrors.successCriteria = '도달 성공 기준은 반복 횟수보다 클 수 없습니다.';
    }
    if (!formData.behaviorType) {
      newErrors.behaviorType = '행동 유형을 선택해주세요.';
    }
    if (!formData.programSuccessCriteria || formData.programSuccessCriteria < 1) {
      newErrors.programSuccessCriteria = '프로그램 성공 기준은 1 이상이어야 합니다.';
    }
    if (!formData.programSuccessMode) {
      newErrors.programSuccessMode = '성공 모드를 선택해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      // 지시자와 상태는 기본값으로 설정
      const programData = {
        ...formData,
        useFreeForm,
        freeFormContent: useFreeForm ? freeFormContent : undefined,
        instructor: '치료사',
        status: '진행중',
        referenceFiles: {
          images: referenceImages,
          videos: referenceVideos
        }
      };
      await onSubmit(studentId, programData);
      onClose();
    } catch (error) {
      console.error('Failed to add program:', error);
      setErrors({ submit: error.message || '프로그램 추가에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="새 프로그램 추가">
      <Form onSubmit={handleSubmit}>
        {/* 템플릿 선택 섹션 */}
        <FormGroup>
          <Label>템플릿에서 선택 (선택사항)</Label>
          <Select
            value={selectedTemplateId}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            disabled={loading}
          >
            <option value="">템플릿을 선택하세요 (직접 입력하려면 비워두세요)</option>
            {programTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.majorCategory} &gt; {template.middleCategory} &gt; {template.minorCategory}
              </option>
            ))}
          </Select>
          <HelpText>
            템플릿을 선택하면 프로그램 정보가 자동으로 입력됩니다. 직접 입력하려면 템플릿을 선택하지 마세요.
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label>대주제 *</Label>
          {!showNewMajorInput ? (
            <FlexRow>
              <FlexGrow>
                <Select
                  name="majorCategory"
                  value={formData.majorCategory}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">대주제를 선택하세요</option>
                  {existingMajorCategories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                  {/* 새로 추가된 카테고리가 기존 목록에 없으면 표시 */}
                  {formData.majorCategory && !existingMajorCategories.includes(formData.majorCategory) && (
                    <option value={formData.majorCategory}>
                      {formData.majorCategory}
                    </option>
                  )}
                  <option value="__new__">+ 새 대주제 추가</option>
                </Select>
              </FlexGrow>
            </FlexRow>
          ) : (
            <FlexRow>
              <FlexGrow>
                <Input
                  type="text"
                  value={newMajorCategory}
                  onChange={(e) => setNewMajorCategory(e.target.value)}
                  placeholder="새 대주제 입력 (예: 언어발달, 사회성발달)"
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddNewMajor();
                    }
                  }}
                />
              </FlexGrow>
              <AddNewButton
                type="button"
                onClick={handleAddNewMajor}
                disabled={loading || !newMajorCategory.trim()}
              >
                추가
              </AddNewButton>
              <Button
                type="button"
                onClick={() => {
                  setShowNewMajorInput(false);
                  setNewMajorCategory('');
                  setFormData(prev => ({ ...prev, majorCategory: '' }));
                }}
                disabled={loading}
                style={{ padding: '10px 16px', backgroundColor: '#6c757d' }}
              >
                취소
              </Button>
            </FlexRow>
          )}
          {errors.majorCategory && <ErrorMessage>{errors.majorCategory}</ErrorMessage>}
          {!showNewMajorInput && (
            <HelpText>기존 대주제를 선택하거나 새 항목을 추가할 수 있습니다.</HelpText>
          )}
        </FormGroup>

        <FormGroup>
          <Label>중주제 *</Label>
          {!showNewMiddleInput ? (
            <FlexRow>
              <FlexGrow>
                <Select
                  name="middleCategory"
                  value={formData.middleCategory}
                  onChange={handleChange}
                  disabled={loading || !formData.majorCategory || formData.majorCategory === '__new__'}
                >
                  <option value="">중주제를 선택하세요</option>
                  {existingMiddleCategories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                  {/* 새로 추가된 카테고리가 기존 목록에 없으면 표시 */}
                  {formData.middleCategory && !existingMiddleCategories.includes(formData.middleCategory) && (
                    <option value={formData.middleCategory}>
                      {formData.middleCategory}
                    </option>
                  )}
                  <option value="__new__">+ 새 중주제 추가</option>
                </Select>
              </FlexGrow>
            </FlexRow>
          ) : (
            <FlexRow>
              <FlexGrow>
                <Input
                  type="text"
                  value={newMiddleCategory}
                  onChange={(e) => setNewMiddleCategory(e.target.value)}
                  placeholder="새 중주제 입력 (예: 수용언어, 표현언어)"
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddNewMiddle();
                    }
                  }}
                />
              </FlexGrow>
              <AddNewButton
                type="button"
                onClick={handleAddNewMiddle}
                disabled={loading || !newMiddleCategory.trim()}
              >
                추가
              </AddNewButton>
              <Button
                type="button"
                onClick={() => {
                  setShowNewMiddleInput(false);
                  setNewMiddleCategory('');
                  setFormData(prev => ({ ...prev, middleCategory: '' }));
                }}
                disabled={loading}
                style={{ padding: '10px 16px', backgroundColor: '#6c757d' }}
              >
                취소
              </Button>
            </FlexRow>
          )}
          {errors.middleCategory && <ErrorMessage>{errors.middleCategory}</ErrorMessage>}
          {!showNewMiddleInput && (
            <HelpText>기존 중주제를 선택하거나 새 항목을 추가할 수 있습니다.</HelpText>
          )}
        </FormGroup>

        <FormGroup>
          <Label>프로그램 *</Label>
          {!showNewMinorInput ? (
            <FlexRow>
              <FlexGrow>
                <Select
                  name="minorCategory"
                  value={formData.minorCategory}
                  onChange={handleChange}
                  disabled={loading || !formData.middleCategory || formData.middleCategory === '__new__'}
                >
                  <option value="">프로그램을 선택하세요</option>
                  {existingMinorCategories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                  {/* 새로 추가된 카테고리가 기존 목록에 없으면 표시 */}
                  {formData.minorCategory && !existingMinorCategories.includes(formData.minorCategory) && (
                    <option value={formData.minorCategory}>
                      {formData.minorCategory}
                    </option>
                  )}
                  <option value="__new__">+ 새 프로그램 추가</option>
                </Select>
              </FlexGrow>
            </FlexRow>
          ) : (
            <FlexRow>
              <FlexGrow>
                <Input
                  type="text"
                  value={newMinorCategory}
                  onChange={(e) => setNewMinorCategory(e.target.value)}
                  placeholder="새 프로그램 입력 (예: 간단한지시따르기)"
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddNewMinor();
                    }
                  }}
                />
              </FlexGrow>
              <AddNewButton
                type="button"
                onClick={handleAddNewMinor}
                disabled={loading || !newMinorCategory.trim()}
              >
                추가
              </AddNewButton>
              <Button
                type="button"
                onClick={() => {
                  setShowNewMinorInput(false);
                  setNewMinorCategory('');
                  setFormData(prev => ({ ...prev, minorCategory: '' }));
                }}
                disabled={loading}
                style={{ padding: '10px 16px', backgroundColor: '#6c757d' }}
              >
                취소
              </Button>
            </FlexRow>
          )}
          {errors.minorCategory && <ErrorMessage>{errors.minorCategory}</ErrorMessage>}
          {!showNewMinorInput && (
            <HelpText>
              기존 프로그램을 선택하면 설정값이 자동으로 채워집니다. 새 항목을 추가할 수도 있습니다.
            </HelpText>
          )}
        </FormGroup>

        <FormGroup>
          <Label>입력 방식 선택</Label>
          <ToggleButtonGroup>
            <ToggleButton
              type="button"
              $active={!useFreeForm}
              onClick={() => setUseFreeForm(false)}
              disabled={loading}
            >
              폼 입력
            </ToggleButton>
            <ToggleButton
              type="button"
              $active={useFreeForm}
              onClick={() => setUseFreeForm(true)}
              disabled={loading}
            >
              줄글로 입력
            </ToggleButton>
          </ToggleButtonGroup>
          <HelpText>
            폼 입력: 각 항목을 개별적으로 입력 | 줄글로 입력: 자유 형식으로 작성
          </HelpText>
        </FormGroup>

        {!useFreeForm ? (
          <>
            <FormGroup>
              <Label>세부 목표 *</Label>
              <Textarea
                name="detailedGoal"
                value={formData.detailedGoal}
                onChange={handleChange}
                placeholder='예: "앉아", "일어나", "이리와" 등 간단한 한 단계 지시를 따를 수 있다'
                disabled={loading}
              />
              {errors.detailedGoal && <ErrorMessage>{errors.detailedGoal}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>지시 방법 *</Label>
              <Textarea
                name="instructionMethod"
                value={formData.instructionMethod}
                onChange={handleChange}
                placeholder="예: 명확하고 간결한 언어로 지시하고 즉각적인 반응을 유도한다"
                disabled={loading}
              />
              {errors.instructionMethod && <ErrorMessage>{errors.instructionMethod}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>촉구 방법 *</Label>
              <Input
                type="text"
                name="promptingMethod"
                value={formData.promptingMethod}
                onChange={handleChange}
                placeholder="예: 신체적 유도 → 제스처 → 독립"
                disabled={loading}
              />
              {errors.promptingMethod && <ErrorMessage>{errors.promptingMethod}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>강화 스케줄 *</Label>
              <Input
                type="text"
                name="reinforcementSchedule"
                value={formData.reinforcementSchedule}
                onChange={handleChange}
                placeholder="예: FR-2 (2회 성공 시 강화)"
                disabled={loading}
              />
              {errors.reinforcementSchedule && <ErrorMessage>{errors.reinforcementSchedule}</ErrorMessage>}
            </FormGroup>
          </>
        ) : (
          <FormGroup>
            <Label>프로그램 내용 *</Label>
            <MarkdownEditor
              value={freeFormContent}
              onChange={setFreeFormContent}
              height={400}
              placeholder='세부목표, 지시방법, 촉구방법, 강화스케줄 등을 작성하세요...\n\n예시:\n# 세부 목표\n- 항목 1\n- 항목 2\n\n# 지시 방법\n내용...'
              folder="program-images"
            />
            {errors.freeFormContent && <ErrorMessage>{errors.freeFormContent}</ErrorMessage>}
          </FormGroup>
        )}

        <FormGroup>
          <Label>반복 횟수 *</Label>
          <Input
            type="number"
            name="repetitionCount"
            value={formData.repetitionCount}
            onChange={handleChange}
            min="1"
            disabled={loading}
          />
          {errors.repetitionCount && <ErrorMessage>{errors.repetitionCount}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>도달 성공 기준 *</Label>
          <Input
            type="number"
            name="successCriteria"
            value={formData.successCriteria}
            onChange={handleChange}
            min="1"
            disabled={loading}
          />
          {errors.successCriteria && <ErrorMessage>{errors.successCriteria}</ErrorMessage>}
          <HelpText>반복 횟수 중 몇 회를 성공해야 목표 달성으로 간주할지 입력하세요.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>행동 유형 *</Label>
          <Select
            name="behaviorType"
            value={formData.behaviorType}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="positive_above">긍정행동 (성공 횟수 ≥ 성공기준)</option>
            <option value="perform_only">수행만 하면 성공 (수행 여부만 기록)</option>
          </Select>
          {errors.behaviorType && <ErrorMessage>{errors.behaviorType}</ErrorMessage>}
          <HelpText>긍정행동은 성공 횟수를 측정하고, 수행만 하면 성공은 수행 여부만 기록합니다.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>프로그램 성공 기준 *</Label>
          <Input
            type="number"
            name="programSuccessCriteria"
            value={formData.programSuccessCriteria}
            onChange={handleChange}
            min="1"
            disabled={loading}
            placeholder="예: 5"
          />
          {errors.programSuccessCriteria && <ErrorMessage>{errors.programSuccessCriteria}</ErrorMessage>}
          <HelpText>몇 회의 세션을 성공해야 프로그램이 완료되는지 입력하세요.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>성공 모드 *</Label>
          <Select
            name="programSuccessMode"
            value={formData.programSuccessMode}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="consecutive">연속 (연속으로 N회 성공)</option>
            <option value="cumulative">누적 (총 N회 성공)</option>
          </Select>
          {errors.programSuccessMode && <ErrorMessage>{errors.programSuccessMode}</ErrorMessage>}
          <HelpText>연속 모드는 연속된 세션 성공을, 누적 모드는 전체 성공 세션 수를 계산합니다.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>참고자료 (선택)</Label>
          <FileUploader
            images={referenceImages}
            videos={referenceVideos}
            onImagesChange={setReferenceImages}
            onVideosChange={setReferenceVideos}
          />
          <HelpText>프로그램 관련 이미지(최대 5개)와 동영상(최대 3개, 각 50MB 이하)을 추가할 수 있습니다.</HelpText>
        </FormGroup>

        {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={loading}>
            취소
          </CancelButton>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? '추가 중...' : '프로그램 추가'}
          </SubmitButton>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default AddProgramModal;
